import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaComments, FaHeadset, FaPaperPlane, FaTimes, FaTrash, FaWhatsapp } from "react-icons/fa";
import "./Chatbot.css";

const welcomeMessage = { from: "bot", text: "Hi there! I’m Thrive’s virtual guide. Whether you’re protecting what matters, planning a trip, or simply getting to know our group, I’ll help you find the right place to start. What can I help you with today?" };
const defaultMessages = [welcomeMessage];

const basePrompts = [
  { label: "Protect something", value: "I need help choosing insurance" },
  { label: "Plan a trip", value: "I would like to plan a trip" },
  { label: "Meet Thrive", value: "Tell me about Thrive Holdings" },
  { label: "Talk to the team", value: "How can I contact Thrive?" },
];

const topicPrompts = {
  insurance: [
    { label: "Get a quote", value: "How do I get an insurance quote?" },
    { label: "Make a claim", value: "I need help with a claim" },
    { label: "Types of cover", value: "What types of insurance cover do you support?" },
  ],
  travel: [
    { label: "Travel estimate", value: "How do I get a travel estimate?" },
    { label: "Visa support", value: "Can you help with a visa?" },
    { label: "Corporate travel", value: "Tell me about corporate travel" },
  ],
  group: [
    { label: "Our companies", value: "Which companies are in the group?" },
    { label: "Our values", value: "What are Thrive's values?" },
    { label: "Careers", value: "How can I work at Thrive?" },
  ],
};

const keywords = {
  insurance: ["insurance", "cover", "coverage", "policy", "premium", "risk", "broker", "claim", "motor", "property", "marine", "liability", "life"],
  travel: ["travel", "trip", "tour", "flight", "ticket", "hotel", "visa", "holiday", "itinerary", "destination", "reservation"],
  group: ["holding", "holdings", "group", "subsidiary", "subsidiaries", "company", "companies", "who are you", "what do you do", "about thrive"],
};

const normalise = (text) => ` ${text.toLowerCase().replace(/[^a-z0-9&']+/g, " ")} `;
const includesAny = (text, words) => words.some((word) => normalise(text).includes(` ${word} `));
const has = (text, ...words) => includesAny(text, words);

const getName = (message) => {
  const match = message.trim().match(/^(?:my name is|i am|i'm|call me)\s+([a-z][a-z'-]{1,20})(?:\s|$)/i);
  if (!match) return null;
  const ignored = ["looking", "trying", "interested", "planning", "travelling", "traveling", "fine", "good"];
  return ignored.includes(match[1].toLowerCase()) ? null : match[1][0].toUpperCase() + match[1].slice(1).toLowerCase();
};

const detectTopic = (message) => {
  if (includesAny(message, keywords.travel)) return "travel";
  if (includesAny(message, keywords.insurance)) return "insurance";
  if (includesAny(message, keywords.group)) return "group";
  return null;
};

const liveAgentActions = [
  { type: "whatsapp", label: "Chat on WhatsApp" },
  { type: "tawk", label: "Live website chat" },
];

const CHATBOT_API_URL = process.env.REACT_APP_CHATBOT_API_URL || "https://thrivenig-backend.vercel.app/api/chatbot/";

const reply = (text, topic = null, name = null, actions = []) => ({ text, topic, name, actions });

const getBotReply = (message, context) => {
  const input = message.toLowerCase();
  const statedName = getName(message);
  const detectedTopic = detectTopic(input);
  const topic = detectedTopic || context.topic;
  const firstName = statedName || context.name;
  const greeting = firstName ? `, ${firstName}` : "";

  if (statedName && normalise(message).split(" ").filter(Boolean).length <= 5) return reply(`Lovely to meet you, ${statedName}! What would you like a hand with—insurance, travel, or learning more about Thrive?`, context.topic, statedName);
  if (has(input, "hello", "hi", "hey", "good morning", "good afternoon", "good evening", "how are you")) return reply(`Hello${greeting}! I’m doing well and ready to help. Are we looking at insurance, planning some travel, or exploring the Thrive group today?`, context.topic, statedName);
  if (has(input, "thanks", "thank you", "thank you very much")) return reply(`You’re very welcome${greeting}! Is there anything else you’d like to sort out while you’re here?`, context.topic, statedName);
  if (has(input, "bye", "goodbye", "see you")) return reply(`It was good chatting with you${greeting}. Whenever you need us, Thrive will be here. Take care!`, context.topic, statedName);
  if (input.includes("404") || input.includes("not found")) return reply("That’s frustrating. The page may have moved or no longer exist. Try the main navigation, or use the Contact Us page and our team will point you in the right direction.", context.topic, statedName);

  if (has(input, "contact", "email", "phone", "whatsapp", "address", "speak to someone", "human", "agent", "live chat")) return reply("Of course. You can continue with a Thrive agent on WhatsApp or use our live website chat. If this is a travel enquiry, it helps to share your destination, preferred dates, number of travellers, and the services you need. Choose the channel that suits you below.", detectedTopic || context.topic, statedName, liveAgentActions);
  if (has(input, "career", "careers", "job", "jobs", "vacancy", "work at thrive", "submit my cv")) return reply("It’s great that you’re considering Thrive. Our Careers page has a CV submission form, so you can introduce yourself and share your experience directly with the team. Are you interested in insurance, travel, or the wider group?", "group", statedName);
  if (has(input, "mission", "vision", "values", "integrity", "customer focus")) return reply("Thrive is built around integrity, customer focus, excellence and progress. Our mission is to build and support businesses that deliver outstanding, customer-centred services, with a vision to become a respected African group known for trusted businesses and superior service.", "group", statedName);
  if (has(input, "team", "management", "board", "director", "leadership")) return reply("Our Board of Directors and Management Team pages introduce the people guiding Thrive. They provide responsible leadership and oversight across the holding company and its specialist businesses. Would you like to know more about the group itself?", "group", statedName);
  if (has(input, "claim", "claims", "loss", "accident", "incident")) return reply("I’m sorry you’re dealing with that. Thrive Insurance Brokers Limited supports clients through claims. Have your policy details and incident information ready, then contact the insurance team through the Contact Us page, WhatsApp, or infoinsurance@thrivenig.com so they can guide you properly.", "insurance", statedName);
  if (input.includes("travel insurance")) return reply("Good question—two Thrive teams can be relevant here. Thrive Insurance Brokers can advise on travel insurance cover, while Thrive Travels & Tours handles flights, hotels, visas, tours and itinerary planning. Do you need the insurance cover, the trip arrangements, or both?", "travel", statedName);

  if (has(input, "quote", "price", "cost", "fee", "estimate", "how much")) {
    if (topic === "travel") return reply("Absolutely. To help the travel team prepare an estimate, share your destination, travel dates, number of travellers, and whether you need flights, hotels, visa assistance, tours, or corporate travel support. You can send those details directly to an agent using either option below.", "travel", statedName, liveAgentActions);
    if (topic === "insurance") return reply("Certainly. For a suitable insurance quote, the team will need the type of cover, what you want to protect, and any existing policy details. What would you like to insure?", "insurance", statedName);
    return reply("I can point you the right way. Is this for an insurance quote or a travel estimate?", null, statedName);
  }

  if (has(input, "yes", "yeah", "sure", "okay", "ok", "please do") && context.topic) {
    if (context.topic === "insurance") return reply("Great. Tell me a little about what you want to protect—for example a vehicle, property, business, trip, or something else—and I’ll guide you to the best next step.", "insurance", statedName);
    if (context.topic === "travel") return reply("Perfect. Where would you like to travel, roughly when, and how many people are going? You can also mention whether you need flights, accommodation, visa help, or a complete itinerary.", "travel", statedName);
    return reply("Great. Would you like to explore Thrive’s companies, leadership, values, careers, or contact details?", "group", statedName);
  }
  if (has(input, "no", "not really", "none")) return reply(`No problem${greeting}. We can take a different direction—would insurance, travel, or general information about Thrive be more useful?`, null, statedName);

  if (detectedTopic === "travel") return reply("That sounds like something Thrive Travels & Tours can help with. The team supports flight reservations and ticketing, hotels, visa assistance, tailor-made holidays, tours and corporate travel management. Tell me what you’re planning, or speak directly with an agent below.", "travel", statedName, liveAgentActions);
  if (detectedTopic === "insurance") return reply("You’re in the right place. Thrive Insurance Brokers supports individuals and organisations with risk advice, policy placement, renewals and claims. What would you like to protect or get help with?", "insurance", statedName);
  if (detectedTopic === "group") return reply("Thrive Holdings is a Nigerian holding company that gives strategic direction, governance and shared support to two specialist businesses: Thrive Insurance Brokers and Thrive Travels & Tours. We’re focused on dependable, customer-led services and long-term value. What part of Thrive interests you most?", "group", statedName);
  return reply(`I want to make sure I understand you properly${greeting}. Could you tell me a little more? I’m best placed to help with insurance and risk, travel planning, or information about Thrive Holdings.`, context.topic, statedName);
};

const Chatbot = ({ cookieAccepted = true }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(defaultMessages);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [context, setContext] = useState({ topic: null, name: null });
  const messagesEndRef = useRef(null);
  const conversationVersionRef = useRef(0);

  const openLiveChannel = (channel) => {
    if (channel === "whatsapp") {
      const introduction = context.name ? `My name is ${context.name}. ` : "";
      const message = context.topic === "travel"
        ? `${introduction}Hello Thrive Travels, I would like help planning a trip.\n\nDestination: \nTravel dates: \nNumber of travellers: \nServices needed (flight, hotel, visa, tour): `
        : `${introduction}Hello Thrive, I was referred by the website virtual guide and would like to speak with an agent.`;
      window.open(`https://wa.me/2348180996418?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
      return;
    }

    if (window.Tawk_API?.maximize) {
      setOpen(false);
      window.Tawk_API.maximize();
      return;
    }

    setMessages((current) => [...current, {
      from: "bot",
      text: "The live chat is still loading or unavailable right now. Please try WhatsApp instead, or call 08180996418.",
      actions: [{ type: "whatsapp", label: "Chat on WhatsApp" }],
    }]);
  };

  const handleSend = async (message = input) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || typing) return;
    const fallbackReply = getBotReply(trimmedMessage, context);
    const userMessage = { from: "user", text: trimmedMessage };
    const requestVersion = conversationVersionRef.current;
    const history = [...messages, userMessage].slice(-12).map((item) => ({
      role: item.from === "user" ? "user" : "assistant",
      content: item.text,
    }));

    setMessages((current) => [...current, userMessage]);
    setInput("");
    setTyping(true);

    let responseText = fallbackReply.text;
    try {
      const response = await axios.post(CHATBOT_API_URL, { messages: history }, { timeout: 30000 });
      if (typeof response.data?.reply === "string" && response.data.reply.trim()) {
        responseText = response.data.reply.trim();
      }
    } catch (error) {
      // The local, website-grounded reply keeps chat available during API outages.
      if (process.env.NODE_ENV === "development") {
        console.info("AI chatbot unavailable; using local response.", error.message);
      }
    }

    if (conversationVersionRef.current === requestVersion) {
      setMessages((current) => [...current, { from: "bot", text: responseText, actions: fallbackReply.actions }]);
      setContext((current) => ({
        topic: fallbackReply.topic === null ? current.topic : fallbackReply.topic,
        name: fallbackReply.name || current.name,
      }));
      setTyping(false);
    }
  };

  const handleClear = () => {
    conversationVersionRef.current += 1;
    setMessages(defaultMessages);
    setContext({ topic: null, name: null });
    setTyping(false);
    setInput("");
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);
  useEffect(() => {
    if (!open) return undefined;
    const handleEsc = (event) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open]);

  const quickPrompts = topicPrompts[context.topic] || basePrompts;

  return <div className={`chatbot-container ${!cookieAccepted ? "hidden-on-mobile" : ""}`}>
    {open ? <section className="chatbot-window" aria-label="Chat with Thrive's virtual guide">
      <div className="chatbot-header">
        <div><span>Thrive Virtual Guide</span><small><i /> Here to help</small></div>
        <button className="chatbot-close" onClick={() => setOpen(false)} aria-label="Close chatbot"><FaTimes /></button>
      </div>
      <div className="chatbot-messages" aria-live="polite">
        {messages.map((message, index) => <div key={index} className={`chatbot-message ${message.from}`}>
          <span>{message.text}</span>
          {message.actions?.length > 0 && <div className="chatbot-actions">
            {message.actions.map((action) => <button key={action.type} type="button" onClick={() => openLiveChannel(action.type)}>
              {action.type === "whatsapp" ? <FaWhatsapp aria-hidden="true" /> : <FaHeadset aria-hidden="true" />}
              {action.label}
            </button>)}
          </div>}
        </div>)}
        {typing && <div className="chatbot-message bot chatbot-typing" aria-label="Thrive is typing"><span /><span /><span /></div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="chatbot-prompts" aria-label="Suggested questions">
        {quickPrompts.map((prompt) => <button key={prompt.label} type="button" disabled={typing} onClick={() => handleSend(prompt.value)}>{prompt.label}</button>)}
      </div>
      <div className="chatbot-input">
        <input type="text" value={input} disabled={typing} onChange={(event) => setInput(event.target.value)} placeholder={typing ? "Thrive is replying..." : "Type your message..."} onKeyDown={(event) => event.key === "Enter" && handleSend()} aria-label="Your message" />
        <button type="button" disabled={typing || !input.trim()} onClick={() => handleSend()} aria-label="Send message"><FaPaperPlane aria-hidden="true" /><span>Send</span></button>
        <button type="button" onClick={handleClear} aria-label="Clear chat" title="Start over"><FaTrash aria-hidden="true" /></button>
      </div>
    </section> : <button className="chatbot-fab" onClick={() => setOpen(true)} aria-label="Open Thrive virtual guide"><FaComments size={24} /> Let’s chat</button>}
  </div>;
};

export default Chatbot;
